import { useCallback, useEffect, useMemo, useState } from "react";
import { Download, Loader2 } from "lucide-react";
import { SiteNav } from "@/components/site/SiteNav";
import { SiteFooter } from "@/components/site/SiteFooter";
import { supabase } from "@/integrations/supabase/client";
import {
  BUILT_INDEX_STEPS,
  BUILT_INDEX_STEP_LABELS,
  type BuiltIndexStep,
} from "@/lib/built-index-analytics";
import {
  COHORT_MILESTONES,
  buildCohorts,
  cohortCsvRows,
  cohortMilestoneLabel,
} from "@/lib/cohort-analytics";
import { FunnelChart } from "@/components/admin/FunnelChart";
import { buildParticipantExport } from "@/lib/participant-export";
import { FunnelAlertRules } from "@/components/admin/FunnelAlertRules";
import { FunnelAlertRunLog } from "@/components/admin/FunnelAlertRunLog";


type Row = {
  id: string;
  created_at: string;
  full_name: string;
  email: string;
  role: string | null;
  built_total: number | null;
  built_tier: string | null;
  disc_type: string | null;
  lovin_score: number | null;
  completed: boolean;
  paid: boolean;
};

type EventRow = {
  id: string;
  occurred_at: string;
  step: string;
  email: string | null;
  session_id: string | null;
  path: string | null;
  user_agent: string | null;
  attribution: Record<string, unknown> | null;
  metadata: Record<string, unknown> | null;
};


const RANGES = [
  { key: "7", label: "Last 7 days" },
  { key: "30", label: "Last 30 days" },
  { key: "90", label: "Last 90 days" },
  { key: "all", label: "All time" },
] as const;
type RangeKey = (typeof RANGES)[number]["key"];

function toCsv(rows: Record<string, unknown>[]): string {
  if (rows.length === 0) return "";
  const keys = Array.from(new Set(rows.flatMap((r) => Object.keys(r))));
  const esc = (v: unknown) =>
    `"${String(v == null ? "" : typeof v === "object" ? JSON.stringify(v) : v).replace(/"/g, '""')}"`;
  return [keys.join(","), ...rows.map((r) => keys.map((k) => esc(r[k])).join(","))].join("\n");
}

function downloadCsv(name: string, rows: Record<string, unknown>[]) {
  const csv = toCsv(rows);
  if (!csv) return;
  const url = URL.createObjectURL(new Blob([csv], { type: "text/csv;charset=utf-8" }));
  const a = document.createElement("a");
  a.href = url;
  a.download = `${name}-${new Date().toISOString().slice(0, 10)}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

const AdminBuiltIndex = () => {
  const [rows, setRows] = useState<Row[]>([]);
  const [events, setEvents] = useState<EventRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [range, setRange] = useState<RangeKey>("30");

  useEffect(() => {
    let active = true;
    setLoading(true);
    const since =
      range === "all"
        ? null
        : new Date(Date.now() - Number(range) * 24 * 60 * 60 * 1000).toISOString();

    const sessionsQuery = supabase
      .from("built_index_sessions")
      .select(
        "id, created_at, full_name, email, role, built_total, built_tier, disc_type, lovin_score, completed, paid",
      )
      .order("created_at", { ascending: false })
      .limit(1000);
    const eventsQuery = supabase
      .from("built_index_events")
      .select("id, occurred_at, step, email, session_id, path, user_agent, attribution, metadata")
      .order("occurred_at", { ascending: false })
      .limit(5000);

    if (since) {
      sessionsQuery.gte("created_at", since);
      eventsQuery.gte("occurred_at", since);
    }

    Promise.all([sessionsQuery, eventsQuery]).then(([s, e]) => {
      if (!active) return;
      const err = s.error?.message ?? e.error?.message ?? null;
      setError(err);
      setRows((s.data ?? []) as Row[]);
      setEvents((e.data ?? []) as EventRow[]);
      setLoading(false);
    });

    return () => {
      active = false;
    };
  }, [range]);

  /** Unique sessions per step — the conversion funnel. */
  const funnel = useMemo(() => {
    const uniques = new Map<string, Set<string>>();
    for (const ev of events) {
      const set = uniques.get(ev.step) ?? new Set<string>();
      set.add(ev.session_id ?? ev.email ?? ev.id);
      uniques.set(ev.step, set);
    }
    const counts = BUILT_INDEX_STEPS.map((step) => ({
      step,
      label: BUILT_INDEX_STEP_LABELS[step as BuiltIndexStep],
      unique: uniques.get(step)?.size ?? 0,
      total: events.filter((e) => e.step === step).length,
    }));
    const top = counts[0]?.unique || counts.find((c) => c.unique > 0)?.unique || 0;
    let prev = 0;
    return counts.map((c) => {
      const stepRate = prev === 0 ? null : Math.round((c.unique / prev) * 1000) / 10;
      const overall = top === 0 ? 0 : Math.round((c.unique / top) * 1000) / 10;
      prev = c.unique || prev;
      return { ...c, stepRate, overall };
    });
  }, [events]);

  /** Signup-week cohorts — conversion at each milestone over time. */
  const cohorts = useMemo(() => buildCohorts(events), [events]);

  const completed = rows.filter((r) => r.completed).length;
  const paid = rows.filter((r) => r.paid).length;

  const exportCohorts = useCallback(() => {
    downloadCsv("built-index-cohorts", cohortCsvRows(cohorts));
  }, [cohorts]);



  const exportFunnel = useCallback(() => {
    downloadCsv(
      "built-index-funnel",
      funnel.map((f) => ({
        step: f.step,
        label: f.label,
        unique_sessions: f.unique,
        total_events: f.total,
        step_conversion_pct: f.stepRate ?? "",
        overall_conversion_pct: f.overall,
        range_days: range,
      })),
    );
  }, [funnel, range]);

  const exportEvents = useCallback(() => {
    downloadCsv(
      "built-index-events",
      events.map((e) => ({
        occurred_at: e.occurred_at,
        step: e.step,
        label: BUILT_INDEX_STEP_LABELS[e.step as BuiltIndexStep] ?? e.step,
        email: e.email ?? "",
        session_id: e.session_id ?? "",
        path: e.path ?? "",
        metadata: e.metadata ?? {},
      })),
    );
  }, [events]);

  const exportParticipants = useCallback(() => {
    downloadCsv("built-index-per-user", buildParticipantExport(rows, events));
  }, [rows, events]);

  const exportSessions = useCallback(() => {
    downloadCsv("built-index-sessions", rows as unknown as Record<string, unknown>[]);
  }, [rows]);

  const btn =
    "inline-flex items-center gap-2 border border-border px-4 py-2 text-xs uppercase tracking-[0.16em] hover:bg-muted/50 transition";

  return (
    <div className="min-h-screen bg-background text-foreground">
      <SiteNav />
      <section className="pt-32 pb-20">
        <div className="container">
          <div className="flex flex-wrap items-end justify-between gap-4">
            <div>
              <h1 className="font-display font-bold uppercase text-3xl md:text-4xl">
                Built Index — Conversion
              </h1>
              <p className="text-muted-foreground mt-2 text-sm">
                {rows.length} started · {completed} completed · {paid} unlocked
              </p>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <label className="sr-only" htmlFor="range">
                Date range
              </label>
              <select
                id="range"
                value={range}
                onChange={(e) => setRange(e.target.value as RangeKey)}
                className="border border-border bg-background px-3 py-2 text-xs uppercase tracking-[0.16em]"
              >
                {RANGES.map((r) => (
                  <option key={r.key} value={r.key}>
                    {r.label}
                  </option>
                ))}
              </select>
              <button type="button" className={btn} onClick={exportFunnel}>
                <Download className="h-4 w-4" /> Funnel CSV
              </button>
              <button type="button" className={btn} onClick={exportCohorts}>
                <Download className="h-4 w-4" /> Cohorts CSV
              </button>

              <button type="button" className={btn} onClick={exportParticipants}>
                <Download className="h-4 w-4" /> Per-user CSV
              </button>
              <button type="button" className={btn} onClick={exportEvents}>
                <Download className="h-4 w-4" /> Events CSV
              </button>
              <button type="button" className={btn} onClick={exportSessions}>
                <Download className="h-4 w-4" /> Sessions CSV
              </button>
            </div>
          </div>

          {loading && (
            <p className="mt-8 flex items-center gap-2 text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" /> Loading…
            </p>
          )}
          {error && (
            <p role="alert" className="mt-8 text-sm text-destructive">
              {error}
            </p>
          )}

          {!loading && !error && (
            <>
              <FunnelAlertRules />
              <FunnelAlertRunLog />

              <div className="mt-12">
                <FunnelChart
                  events={events}
                  onExport={(r) => downloadCsv("built-index-funnel-drilldown", r)}
                />
              </div>

              <h2 className="font-display uppercase text-xl mt-12">Funnel by step</h2>
              <div className="mt-4 overflow-x-auto border border-border">
                <table className="w-full text-sm">
                  <thead className="bg-muted/40 text-left">
                    <tr>
                      {["Step", "Unique", "Events", "Step conv.", "Of top", ""].map((h) => (
                        <th key={h} className="px-3 py-2 font-semibold whitespace-nowrap">
                          {h}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {funnel.map((f) => (
                      <tr key={f.step} className="border-t border-border">
                        <td className="px-3 py-2 whitespace-nowrap">{f.label}</td>
                        <td className="px-3 py-2 font-semibold">{f.unique}</td>
                        <td className="px-3 py-2 text-muted-foreground">{f.total}</td>
                        <td className="px-3 py-2">{f.stepRate == null ? "—" : `${f.stepRate}%`}</td>
                        <td className="px-3 py-2">{f.overall}%</td>
                        <td className="px-3 py-2 w-1/3">
                          <div className="h-2 w-full bg-muted">
                            <div
                              className="h-2 bg-orange"
                              style={{ width: `${Math.min(100, f.overall)}%` }}
                            />
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <h2 className="font-display uppercase text-xl mt-12">Cohorts by signup week</h2>
              <p className="text-muted-foreground mt-2 text-sm max-w-2xl">
                Each visitor is counted in the week they entered the funnel, so a purchase
                weeks later still credits the cohort that produced it. Read down a column to
                see whether that step is converting better or worse over time.
              </p>
              <div className="mt-4 overflow-x-auto border border-border">
                <table className="w-full text-sm">
                  <thead className="bg-muted/40 text-left">
                    <tr>
                      <th className="px-3 py-2 font-semibold whitespace-nowrap">Week of</th>
                      <th className="px-3 py-2 font-semibold whitespace-nowrap">Cohort</th>
                      {COHORT_MILESTONES.map((step) => (
                        <th key={step} className="px-3 py-2 font-semibold whitespace-nowrap">
                          {cohortMilestoneLabel(step)}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {cohorts.map((c) => (
                      <tr key={c.weekStart} className="border-t border-border">
                        <td className="px-3 py-2 whitespace-nowrap">{c.label}</td>
                        <td className="px-3 py-2 font-semibold">{c.size}</td>
                        {COHORT_MILESTONES.map((step) => {
                          const rate = c.rates[step] ?? 0;
                          return (
                            <td key={step} className="px-3 py-2 whitespace-nowrap">
                              <span
                                className="inline-block min-w-[3.5rem] px-2 py-1 text-center"
                                style={{
                                  backgroundColor: `hsl(var(--orange) / ${Math.min(1, rate / 100) * 0.28})`,
                                }}
                              >
                                {rate}%
                              </span>
                              <span className="text-muted-foreground ml-2">
                                {c.reached[step] ?? 0}
                              </span>
                            </td>
                          );
                        })}
                      </tr>
                    ))}
                    {cohorts.length === 0 && (
                      <tr>
                        <td
                          className="px-3 py-6 text-muted-foreground"
                          colSpan={COHORT_MILESTONES.length + 2}
                        >
                          No cohorts yet — they appear once funnel events are recorded.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>

              <h2 className="font-display uppercase text-xl mt-12">Sessions</h2>

              <div className="mt-4 overflow-x-auto border border-border">
                <table className="w-full text-sm">
                  <thead className="bg-muted/40 text-left">
                    <tr>
                      {[
                        "Date",
                        "Name",
                        "Email",
                        "Role",
                        "Built",
                        "Tier",
                        "DISC",
                        "LOVIN",
                        "Status",
                      ].map((h) => (
                        <th key={h} className="px-3 py-2 font-semibold whitespace-nowrap">
                          {h}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((r) => (
                      <tr key={r.id} className="border-t border-border align-top">
                        <td className="px-3 py-2 whitespace-nowrap">
                          {new Date(r.created_at).toLocaleDateString()}
                        </td>
                        <td className="px-3 py-2">{r.full_name}</td>
                        <td className="px-3 py-2">{r.email}</td>
                        <td className="px-3 py-2">{r.role ?? "—"}</td>
                        <td className="px-3 py-2">{r.built_total ?? "—"}</td>
                        <td className="px-3 py-2">{r.built_tier ?? "—"}</td>
                        <td className="px-3 py-2">{r.disc_type ?? "—"}</td>
                        <td className="px-3 py-2">{r.lovin_score ?? "—"}</td>
                        <td className="px-3 py-2 whitespace-nowrap">
                          {r.paid ? "Unlocked" : r.completed ? "Completed" : "In progress"}
                        </td>
                      </tr>
                    ))}
                    {rows.length === 0 && (
                      <tr>
                        <td className="px-3 py-6 text-muted-foreground" colSpan={9}>
                          No sessions yet.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>

              <h2 className="font-display uppercase text-xl mt-12">Recent step events</h2>
              <div className="mt-4 overflow-x-auto border border-border">
                <table className="w-full text-sm">
                  <thead className="bg-muted/40 text-left">
                    <tr>
                      {["When", "Step", "Email", "Path"].map((h) => (
                        <th key={h} className="px-3 py-2 font-semibold whitespace-nowrap">
                          {h}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {events.slice(0, 100).map((e) => (
                      <tr key={e.id} className="border-t border-border">
                        <td className="px-3 py-2 whitespace-nowrap">
                          {new Date(e.occurred_at).toLocaleString()}
                        </td>
                        <td className="px-3 py-2 whitespace-nowrap">
                          {BUILT_INDEX_STEP_LABELS[e.step as BuiltIndexStep] ?? e.step}
                        </td>
                        <td className="px-3 py-2">{e.email ?? "—"}</td>
                        <td className="px-3 py-2 text-muted-foreground">{e.path ?? "—"}</td>
                      </tr>
                    ))}
                    {events.length === 0 && (
                      <tr>
                        <td className="px-3 py-6 text-muted-foreground" colSpan={4}>
                          No funnel events recorded yet.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>
      </section>
      <SiteFooter />
    </div>
  );
};

export default AdminBuiltIndex;
